import React, { useEffect } from 'react';

import { useAtom, useSetAtom } from 'jotai';

import {
  useAccountAndContract,
  useLoanSummary,
} from '@consumer-frontend/nc-api';
import { initialAlertBannerProps, productTypeDisplayFromType } from '@consumer-frontend/nc-utils';
import { Container } from '@consumer-frontend/shared-ui';

import { NcContractHTML } from './NcContractHTML';
import { accountIdAtom, alertBannerAtom } from '../../store';
import { NcAlertBanner } from '../NcAlertBanner';
import { NcContractHeader } from '../NcContractHeader';
import { NcError } from '../NcError';
import { NcLoadingSpinner } from '../NcLoadingSpinner';

import '@consumer-bootstrap/brands/netcredit/contract/contract-page.scss';

export type NcContractT = React.FC;

export const contractLoadingError = 'There was an issue loading your contract.';

export const NcContract: NcContractT = () => {
  const [accountId] = useAtom(accountIdAtom);
  const setAlertBanner = useSetAtom(alertBannerAtom);

  const {
    accountInfo,
    contract,
    contractError,
    isAccountInfoLoading,
    isContractLoading,
  } = useAccountAndContract(accountId);
  const { loanNumber, loanType } = accountInfo || {};

  const {
    loanSummary,
    error: loanError,
    isLoading: isLoanLoading,
  } = useLoanSummary(accountId, loanNumber);
  const { hasDrawFeeSaver, hasStatementBalanceFeeSaver } = loanSummary || {};

  // Ensure alertBanner is empty. Only needs to run on initial page load, hence empty dependency array.
  useEffect(
    () => setAlertBanner(initialAlertBannerProps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // View helper flags
  const isLoading = isAccountInfoLoading || isContractLoading || isLoanLoading;
  const hasFeeSaver = !!(hasDrawFeeSaver || hasStatementBalanceFeeSaver);
  const hasLoanError = loanError && !isLoanLoading;
  const hasContractError = (contractError || !contract?.body) && !isLoading;

  if (isLoading) {
    return (
      <Container className="nc-contract-page" hasBoxShadow>
        <h1 className="h2">Contract</h1>
        <NcLoadingSpinner />
      </Container>
    );
  }

  return (
    <Container className="nc-contract-page" hasBoxShadow>

      <h1 className="h2">Contract</h1>

      <NcAlertBanner />

      {hasContractError && <NcError displaySupportText message={contractLoadingError} />}

      {contract?.body && loanNumber && (
        <>
          <NcContractHeader
            accountId={accountId}
            loanNumber={loanNumber}
            productTypeDisplay={productTypeDisplayFromType(loanType)}
            showFeeSaverBanner={hasFeeSaver && !hasLoanError}
          />

          <hr className="contract-divider" />

          <NcContractHTML
            body={contract.body}
            brandSignature={contract.brand_signature}
            customerSignature={contract.customer_signature}
            productTypeDisplay={productTypeDisplayFromType(loanType)}
          />
        </>
      )}
    </Container>
  );
};
